import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { AssetDeclaration, AssetLine, DeclarationStatus, IncomeLine, LiabilityLine, OfficerCategory, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge, Tabs } from '../../ui/components';

const REGISTRAR_ROLES = ['DECLARATION_REGISTRAR'];
const OFFICER_CATEGORIES: OfficerCategory[] = ['MINISTER', 'NATIONAL_ASSEMBLY_MEMBER', 'CONSTITUTIONAL_APPOINTEE', 'PRINCIPAL_SECRETARY', 'SENIOR_PUBLIC_OFFICER', 'OTHER_DESIGNATED'];
const STATUSES: DeclarationStatus[] = ['SUBMITTED', 'UNDER_REVIEW', 'FLAGGED', 'CLEARED', 'NON_COMPLIANT'];

/** GET/POST /api/v1/m40/asset-declarations — registrar review queue + self-service filing. */
export function AssetDeclarations() {
  const { persona } = useAuth();
  const isRegistrar = persona?.roles.includes('DECLARATION_REGISTRAR') ?? false;
  const navigate = useNavigate();
  const [tab, setTab] = useState<'mine' | 'registrar'>(isRegistrar ? 'registrar' : 'mine');
  const [showCreate, setShowCreate] = useState(false);

  return (
    <div>
      <PageHeader
        title="Asset Declarations"
        subtitle="Public officers' asset, liability, and income declarations — registrar review and (for senior office holders) gazette disclosure of the fact-of-filing only."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + File a declaration
          </button>
        }
      />
      {isRegistrar ? (
        <Tabs tabs={[{ key: 'mine', label: 'My declarations' }, { key: 'registrar', label: 'Registrar review queue' }]} value={tab} onChange={setTab} />
      ) : null}
      {tab === 'registrar' && isRegistrar ? <RegistrarQueue navigate={navigate} /> : <MinePanel navigate={navigate} />}
      {showCreate ? (
        <FileDeclarationModal
          isRegistrar={isRegistrar}
          onClose={() => setShowCreate(false)}
          onDone={(created) => {
            setShowCreate(false);
            navigate(`/ombudsman/asset-declarations/${created.id}`);
          }}
        />
      ) : null}
    </div>
  );
}

function RegistrarQueue({ navigate }: { navigate: (path: string) => void }) {
  const [status, setStatus] = useState<DeclarationStatus | ''>('');
  const [q, setQ] = useState('');
  const qp = new URLSearchParams({ limit: '50' });
  if (status) qp.set('status', status);
  if (q.trim()) qp.set('q', q.trim());
  const listQ = useApiQuery<Page<AssetDeclaration>>(`/api/v1/m40/asset-declarations?${qp.toString()}`);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="Officer / MDA / ref contains" className="min-w-[220px] flex-1">
          <input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} />
        </Field>
        <Field label="Status" className="!mb-0">
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value as DeclarationStatus | '')}>
            <option value="">All</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No declarations match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Officer</th>
                <th className="table-head">Category</th>
                <th className="table-head">FY</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((d) => (
                <tr key={d.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/ombudsman/asset-declarations/${d.id}`)}>
                  <td className="table-cell font-mono text-xs">{d.refNo}</td>
                  <td className="table-cell font-medium">{d.officerName}</td>
                  <td className="table-cell text-xs">{d.officerCategory.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-xs">{d.financialYear}</td>
                  <td className="table-cell">
                    <StatusBadge status={d.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function MinePanel({ navigate }: { navigate: (path: string) => void }) {
  const { persona } = useAuth();
  const mineQ = useApiQuery<Page<AssetDeclaration>>(persona?.nin ? '/api/v1/m40/asset-declarations/me?limit=50' : null);

  if (!persona?.nin) {
    return <RoleNotice roles={['a verified NIN']} />;
  }

  return (
    <div>
      {mineQ.loading ? (
        <Spinner />
      ) : mineQ.error ? (
        <ErrorNote message={mineQ.error} />
      ) : (mineQ.data?.items.length ?? 0) === 0 ? (
        <EmptyState title="You have not filed a declaration" />
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Financial year</th>
                <th className="table-head">Status</th>
                <th className="table-head">Published</th>
              </tr>
            </thead>
            <tbody>
              {mineQ.data!.items.map((d) => (
                <tr key={d.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/ombudsman/asset-declarations/${d.id}`)}>
                  <td className="table-cell font-mono text-xs">{d.refNo}</td>
                  <td className="table-cell text-xs">{d.financialYear}</td>
                  <td className="table-cell">
                    <StatusBadge status={d.status} />
                  </td>
                  <td className="table-cell text-xs">{d.published ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FileDeclarationModal({ isRegistrar, onClose, onDone }: { isRegistrar: boolean; onClose: () => void; onDone: (d: AssetDeclaration) => void }) {
  const [officerNin, setOfficerNin] = useState('');
  const [officerName, setOfficerName] = useState('');
  const [officerCategory, setOfficerCategory] = useState<OfficerCategory>('SENIOR_PUBLIC_OFFICER');
  const [mdaCode, setMdaCode] = useState('');
  const [position, setPosition] = useState('');
  const [financialYear, setFinancialYear] = useState(String(new Date().getFullYear() - 1));
  const [assets, setAssets] = useState<AssetLine[]>([{ type: 'REAL_ESTATE', description: '', estimatedValueScr: 0 }]);
  const [liabilities, setLiabilities] = useState<LiabilityLine[]>([]);
  const [incomeSources, setIncomeSources] = useState<IncomeLine[]>([{ source: 'SALARY', description: '' }]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<AssetDeclaration>('/api/v1/m40/asset-declarations', {
        officerNin: isRegistrar && officerNin ? officerNin : undefined,
        officerName,
        officerCategory,
        mdaCode,
        position,
        financialYear: Number.parseInt(financialYear, 10),
        assets: assets.filter((a) => a.description.trim()),
        liabilities: liabilities.filter((l) => l.description.trim()),
        incomeSources: incomeSources.filter((i) => i.source.trim()),
      });
      onDone(created);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to file declaration');
    } finally {
      setBusy(false);
    }
  };

  const valid = officerName.trim().length >= 2 && mdaCode.trim().length >= 2 && position.trim().length >= 2;

  return (
    <Modal title={isRegistrar ? 'File a declaration (registrar-assisted)' : 'File my asset declaration'} onClose={onClose} wide>
      <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Officer name">
            <input className="field-input" value={officerName} onChange={(e) => setOfficerName(e.target.value)} />
          </Field>
          {isRegistrar ? (
            <Field label="Officer NIN (registrar-assisted; blank = your own)">
              <input className="field-input" value={officerNin} onChange={(e) => setOfficerNin(e.target.value)} />
            </Field>
          ) : (
            <Field label="Officer category">
              <select className="field-input" value={officerCategory} onChange={(e) => setOfficerCategory(e.target.value as OfficerCategory)}>
                {OFFICER_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c.replaceAll('_', ' ')}
                  </option>
                ))}
              </select>
            </Field>
          )}
        </div>
        {isRegistrar ? (
          <Field label="Officer category">
            <select className="field-input" value={officerCategory} onChange={(e) => setOfficerCategory(e.target.value as OfficerCategory)}>
              {OFFICER_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        <div className="grid grid-cols-3 gap-3">
          <Field label="MDA code">
            <input className="field-input" value={mdaCode} onChange={(e) => setMdaCode(e.target.value)} placeholder="e.g. M20-INFRA" />
          </Field>
          <Field label="Position">
            <input className="field-input" value={position} onChange={(e) => setPosition(e.target.value)} />
          </Field>
          <Field label="Financial year">
            <input type="number" className="field-input" value={financialYear} onChange={(e) => setFinancialYear(e.target.value)} />
          </Field>
        </div>
        <p className="text-xs text-slate-500">
          MDA/position are best-effort enriched from StaffDesk at submission time if a match exists; the values entered here are a self-attested
          fallback.
        </p>

        <LineEditor
          title="Assets"
          rows={assets}
          onAdd={() => setAssets((r) => [...r, { type: 'OTHER', description: '', estimatedValueScr: 0 }])}
          render={(row, i) => (
            <div className="mb-1.5 flex gap-1.5">
              <input className="field-input !w-32" placeholder="Type" value={row.type} onChange={(e) => setAssets((r) => r.map((x, idx) => (idx === i ? { ...x, type: e.target.value } : x)))} />
              <input className="field-input" placeholder="Description" value={row.description} onChange={(e) => setAssets((r) => r.map((x, idx) => (idx === i ? { ...x, description: e.target.value } : x)))} />
              <input
                type="number"
                className="field-input !w-32"
                placeholder="Value SCR"
                value={row.estimatedValueScr || ''}
                onChange={(e) => setAssets((r) => r.map((x, idx) => (idx === i ? { ...x, estimatedValueScr: Number.parseFloat(e.target.value) || 0 } : x)))}
              />
              <button className="shrink-0 text-slate-400 hover:text-rose-600" onClick={() => setAssets((r) => r.filter((_, idx) => idx !== i))}>
                ✕
              </button>
            </div>
          )}
        />
        <LineEditor
          title="Liabilities"
          rows={liabilities}
          onAdd={() => setLiabilities((r) => [...r, { type: 'OTHER', description: '', amountScr: 0 }])}
          render={(row, i) => (
            <div className="mb-1.5 flex gap-1.5">
              <input className="field-input !w-32" placeholder="Type" value={row.type} onChange={(e) => setLiabilities((r) => r.map((x, idx) => (idx === i ? { ...x, type: e.target.value } : x)))} />
              <input className="field-input" placeholder="Description" value={row.description} onChange={(e) => setLiabilities((r) => r.map((x, idx) => (idx === i ? { ...x, description: e.target.value } : x)))} />
              <input
                type="number"
                className="field-input !w-32"
                placeholder="Amount SCR"
                value={row.amountScr || ''}
                onChange={(e) => setLiabilities((r) => r.map((x, idx) => (idx === i ? { ...x, amountScr: Number.parseFloat(e.target.value) || 0 } : x)))}
              />
              <button className="shrink-0 text-slate-400 hover:text-rose-600" onClick={() => setLiabilities((r) => r.filter((_, idx) => idx !== i))}>
                ✕
              </button>
            </div>
          )}
        />
        <LineEditor
          title="Income sources"
          rows={incomeSources}
          onAdd={() => setIncomeSources((r) => [...r, { source: '', description: '' }])}
          render={(row, i) => (
            <div className="mb-1.5 flex gap-1.5">
              <input className="field-input !w-40" placeholder="Source" value={row.source} onChange={(e) => setIncomeSources((r) => r.map((x, idx) => (idx === i ? { ...x, source: e.target.value } : x)))} />
              <input
                className="field-input"
                placeholder="Description (optional)"
                value={row.description ?? ''}
                onChange={(e) => setIncomeSources((r) => r.map((x, idx) => (idx === i ? { ...x, description: e.target.value } : x)))}
              />
              <button className="shrink-0 text-slate-400 hover:text-rose-600" onClick={() => setIncomeSources((r) => r.filter((_, idx) => idx !== i))}>
                ✕
              </button>
            </div>
          )}
        />

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !valid}>
            {busy ? 'Filing…' : 'File declaration'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function LineEditor<T>({ title, rows, onAdd, render }: { title: string; rows: T[]; onAdd: () => void; render: (row: T, i: number) => React.ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-600">
          {title} ({rows.length})
        </span>
        <button className="text-xs font-semibold text-brand-700 hover:underline" onClick={onAdd}>
          + Add
        </button>
      </div>
      {rows.map((row, i) => render(row, i))}
    </div>
  );
}
